import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { playgroundEvaluateMutationKeys, playgroundEvaluateQueryKeys } from "./queryKeys";
import { handlePlaygroundEvaluateResponse } from "./response";
import type {
  PlaygroundEvaluateDraftResponse,
  PlaygroundEvaluateRunResponse,
  PlaygroundEvaluateSettings,
  PromotePlaygroundPromptVersionResponse,
  PlaygroundEvaluateSettingsRequest,
  PlaygroundEvaluateSheetReferenceResponse,
  PlaygroundEvaluateStateResponse,
  UpdateEvaluateSheetReferenceRequest,
  PlaygroundEvaluateExportPrompt,
} from "./types";

import { ENDPOINTS } from "@/api/application-api";
import type { GridResponse } from "@/queries/smart-tables/types";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayErrorToast } from "@/utils/toast";

export * from "./queryKeys";
export * from "./types";

interface EvaluateHookArgs {
  userToken: string;
  workspaceId: number;
  sessionId: string;
}

const mutationErrorToast = (error: Error) => displayErrorToast(error);
const invalidateEvaluateState = (
  queryClient: ReturnType<typeof useQueryClient>,
  workspaceId: number,
  sessionId: string,
) =>
  queryClient.invalidateQueries({
    queryKey: playgroundEvaluateQueryKeys.state(workspaceId, sessionId),
  });

const upsertEvaluateSheetReference = (
  references: PlaygroundEvaluateStateResponse["sheet_references"],
  reference: PlaygroundEvaluateSheetReferenceResponse["sheet_reference"],
) => {
  const existingIndex = references.findIndex(candidate => candidate.id === reference.id);
  if (existingIndex === -1) {
    return [...references, reference];
  }

  const nextReferences = [...references];
  nextReferences[existingIndex] = reference;
  return nextReferences;
};

const applyDraftResponseToEvaluateState = (
  queryClient: ReturnType<typeof useQueryClient>,
  workspaceId: number,
  sessionId: string,
  response: PlaygroundEvaluateDraftResponse,
) => {
  queryClient.setQueryData<PlaygroundEvaluateStateResponse>(
    playgroundEvaluateQueryKeys.state(workspaceId, sessionId),
    current =>
      current
        ? {
            ...current,
            settings: response.settings,
            sheet_references: upsertEvaluateSheetReference(
              current.sheet_references,
              response.sheet_reference,
            ),
          }
        : current,
  );
};

export const usePlaygroundEvaluateState = ({
  userToken,
  workspaceId,
  sessionId,
}: EvaluateHookArgs) =>
  useQuery<PlaygroundEvaluateStateResponse>({
    queryKey: playgroundEvaluateQueryKeys.state(workspaceId, sessionId),
    queryFn: () =>
      fetch(ENDPOINTS.playground_evaluate(workspaceId, sessionId), {
        headers: jsonAuthHeaders(userToken),
      }).then(handlePlaygroundEvaluateResponse<PlaygroundEvaluateStateResponse>),
    enabled: Boolean(userToken && workspaceId && sessionId),
  });

export const usePatchPlaygroundEvaluateSettings = ({
  userToken,
  workspaceId,
  sessionId,
}: EvaluateHookArgs) => {
  const queryClient = useQueryClient();
  return useMutation<
    { success: true; settings: PlaygroundEvaluateSettings },
    Error,
    PlaygroundEvaluateSettingsRequest
  >({
    mutationFn: body =>
      fetch(ENDPOINTS.playground_evaluate_settings(workspaceId, sessionId), {
        method: "PATCH",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(body),
      }).then(
        handlePlaygroundEvaluateResponse<{
          success: true;
          settings: PlaygroundEvaluateSettings;
        }>,
      ),
    onSuccess: response => {
      queryClient.setQueryData<PlaygroundEvaluateStateResponse>(
        playgroundEvaluateQueryKeys.state(workspaceId, sessionId),
        current => (current ? { ...current, settings: response.settings } : current),
      );
      void invalidateEvaluateState(queryClient, workspaceId, sessionId);
    },
    onError: mutationErrorToast,
  });
};

export const useEvaluateSheetGrid = ({
  userToken,
  workspaceId,
  smartTableId,
  sheetId,
  limit,
}: {
  userToken: string;
  workspaceId: number;
  smartTableId: string;
  sheetId: string;
  limit: number;
}) =>
  useQuery<GridResponse>({
    queryKey: playgroundEvaluateQueryKeys.sheetGrid(workspaceId, smartTableId, sheetId, limit),
    queryFn: () => {
      const params = new URLSearchParams({
        include_columns: "true",
        include_row_count: "true",
        start_row: "0",
        end_row: String(limit),
        limit: String(limit),
      });
      return fetch(
        `${ENDPOINTS.smart_table_grid(workspaceId, smartTableId, sheetId)}?${params.toString()}`,
        {
          headers: jsonAuthHeaders(userToken),
        },
      ).then(handleResponse);
    },
    enabled: Boolean(userToken && workspaceId && smartTableId && sheetId && limit),
  });

export const useCreatePlaygroundEvaluateDraft = ({
  userToken,
  workspaceId,
  sessionId,
}: EvaluateHookArgs) => {
  const queryClient = useQueryClient();
  return useMutation<
    PlaygroundEvaluateDraftResponse,
    Error,
    { prompt_registry_id?: number; prompt_version_number?: number; create_new?: boolean }
  >({
    mutationFn: body =>
      fetch(ENDPOINTS.playground_evaluate_draft(workspaceId, sessionId), {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(body),
      }).then(handleResponse),
    onSuccess: response => {
      applyDraftResponseToEvaluateState(queryClient, workspaceId, sessionId, response);
      void invalidateEvaluateState(queryClient, workspaceId, sessionId);
    },
  });
};

export const useExportPlaygroundEvaluateDraft = ({
  userToken,
  workspaceId,
  sessionId,
}: EvaluateHookArgs) => {
  const queryClient = useQueryClient();
  return useMutation<
    PlaygroundEvaluateDraftResponse,
    Error,
    {
      referenceId: string;
      folder_id: number | null;
      title?: string;
      prompt: PlaygroundEvaluateExportPrompt;
    }
  >({
    mutationFn: ({ referenceId, ...body }) =>
      fetch(
        ENDPOINTS.playground_evaluate_sheet_reference_export(workspaceId, sessionId, referenceId),
        {
          method: "POST",
          headers: jsonAuthHeaders(userToken),
          body: JSON.stringify(body),
        },
      ).then(handleResponse),
    onSuccess: response => {
      applyDraftResponseToEvaluateState(queryClient, workspaceId, sessionId, response);
      void invalidateEvaluateState(queryClient, workspaceId, sessionId);
      void queryClient.invalidateQueries({ queryKey: ["smart-tables", workspaceId, "list"] });
      void queryClient.invalidateQueries({ queryKey: ["folder-entities", String(workspaceId)] });
      if (response.prompt_version) {
        void queryClient.invalidateQueries({
          predicate: query =>
            JSON.stringify(query.queryKey).includes(ENDPOINTS.prompt_registry_objects),
        });
        void queryClient.invalidateQueries({ queryKey: ["snippet-usage"] });
      }
    },
  });
};

export const useCreateEvaluateSheetReference = ({
  userToken,
  workspaceId,
  sessionId,
}: EvaluateHookArgs) => {
  const queryClient = useQueryClient();
  return useMutation<
    PlaygroundEvaluateSheetReferenceResponse,
    Error,
    { smart_table_id: string; sheet_id: string }
  >({
    mutationFn: body =>
      fetch(ENDPOINTS.playground_evaluate_sheet_references(workspaceId, sessionId), {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(body),
      }).then(handlePlaygroundEvaluateResponse<PlaygroundEvaluateSheetReferenceResponse>),
    onSuccess: response => {
      queryClient.setQueryData<PlaygroundEvaluateStateResponse>(
        playgroundEvaluateQueryKeys.state(workspaceId, sessionId),
        current =>
          current
            ? {
                ...current,
                sheet_references: upsertEvaluateSheetReference(
                  current.sheet_references,
                  response.sheet_reference,
                ),
              }
            : current,
      );
      void invalidateEvaluateState(queryClient, workspaceId, sessionId);
    },
    onError: mutationErrorToast,
  });
};

export const useUpdateEvaluateSheetReference = ({
  userToken,
  workspaceId,
  sessionId,
}: EvaluateHookArgs) => {
  const queryClient = useQueryClient();
  return useMutation<
    PlaygroundEvaluateSheetReferenceResponse,
    Error,
    { referenceId: string; body: UpdateEvaluateSheetReferenceRequest }
  >({
    mutationKey: playgroundEvaluateMutationKeys.sheetReferenceUpdate(workspaceId, sessionId),
    mutationFn: ({ referenceId, body }) =>
      fetch(ENDPOINTS.playground_evaluate_sheet_references(workspaceId, sessionId, referenceId), {
        method: "PATCH",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(body),
      }).then(handlePlaygroundEvaluateResponse<PlaygroundEvaluateSheetReferenceResponse>),
    onSuccess: (data, { referenceId }) => {
      queryClient.setQueryData<PlaygroundEvaluateStateResponse>(
        playgroundEvaluateQueryKeys.state(workspaceId, sessionId),
        current =>
          current
            ? {
                ...current,
                sheet_references: current.sheet_references.map(reference =>
                  reference.id === referenceId ? data.sheet_reference : reference,
                ),
              }
            : current,
      );
      void invalidateEvaluateState(queryClient, workspaceId, sessionId);
    },
    onError: mutationErrorToast,
  });
};

export const useDeleteEvaluateSheetReference = ({
  userToken,
  workspaceId,
  sessionId,
}: EvaluateHookArgs) => {
  const queryClient = useQueryClient();
  return useMutation<{ success: true }, Error, string>({
    mutationFn: referenceId =>
      fetch(ENDPOINTS.playground_evaluate_sheet_references(workspaceId, sessionId, referenceId), {
        method: "DELETE",
        headers: jsonAuthHeaders(userToken),
      }).then(handlePlaygroundEvaluateResponse<{ success: true }>),
    onSuccess: () => void invalidateEvaluateState(queryClient, workspaceId, sessionId),
    onError: mutationErrorToast,
  });
};

export const useRunEvaluateSheetReference = ({
  userToken,
  workspaceId,
  sessionId,
}: EvaluateHookArgs) => {
  const queryClient = useQueryClient();
  return useMutation<
    PlaygroundEvaluateRunResponse,
    Error,
    {
      referenceId: string;
      row_scope: { mode: "first_n"; limit: number };
      input_variable_sets: Record<string, unknown>[];
    }
  >({
    mutationFn: ({ referenceId, ...body }) =>
      fetch(
        ENDPOINTS.playground_evaluate_sheet_reference_runs(workspaceId, sessionId, referenceId),
        {
          method: "POST",
          headers: jsonAuthHeaders(userToken),
          body: JSON.stringify(body),
        },
      ).then(handlePlaygroundEvaluateResponse<PlaygroundEvaluateRunResponse>),
    onSuccess: data => {
      void invalidateEvaluateState(queryClient, workspaceId, sessionId);
      void queryClient.setQueryData(
        playgroundEvaluateQueryKeys.run(workspaceId, sessionId, data.run.id),
        data,
      );
    },
    onError: mutationErrorToast,
  });
};

export const useCancelEvaluateRun = ({ userToken, workspaceId, sessionId }: EvaluateHookArgs) => {
  const queryClient = useQueryClient();
  return useMutation<PlaygroundEvaluateRunResponse, Error, string>({
    mutationFn: runId =>
      fetch(ENDPOINTS.playground_evaluate_run_cancel(workspaceId, sessionId, runId), {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
      }).then(handlePlaygroundEvaluateResponse<PlaygroundEvaluateRunResponse>),
    onSuccess: data => {
      queryClient.setQueryData(
        playgroundEvaluateQueryKeys.run(workspaceId, sessionId, data.run.id),
        data,
      );
      void invalidateEvaluateState(queryClient, workspaceId, sessionId);
    },
    onError: mutationErrorToast,
  });
};

export const usePromotePlaygroundPromptVersion = ({
  userToken,
  workspaceId,
  sessionId,
}: EvaluateHookArgs) => {
  const queryClient = useQueryClient();
  return useMutation<
    PromotePlaygroundPromptVersionResponse,
    Error,
    {
      referenceId: string;
      run_id: string;
      prompt_id: number;
      parent_version_id: number;
      smartTableId: string;
      sheetId: string;
    }
  >({
    mutationFn: ({ referenceId, run_id, prompt_id, parent_version_id }) =>
      fetch(
        ENDPOINTS.playground_evaluate_sheet_reference_promote(workspaceId, sessionId, referenceId),
        {
          method: "POST",
          headers: jsonAuthHeaders(userToken),
          body: JSON.stringify({ run_id, prompt_id, parent_version_id }),
        },
      ).then(handlePlaygroundEvaluateResponse<PromotePlaygroundPromptVersionResponse>),
    onSuccess: (_, variables) => {
      void invalidateEvaluateState(queryClient, workspaceId, sessionId);
      void queryClient.invalidateQueries({
        queryKey: playgroundEvaluateQueryKeys.sheetGrid(
          workspaceId,
          variables.smartTableId,
          variables.sheetId,
          1,
        ),
      });
      void queryClient.invalidateQueries({ queryKey: ["changelog"] });
      void queryClient.invalidateQueries({ queryKey: ["snippet-usage"] });
    },
  });
};

export const useEvaluateRunDetail = ({
  userToken,
  workspaceId,
  sessionId,
  runId,
}: EvaluateHookArgs & { runId?: string | null }) => {
  const queryClient = useQueryClient();

  return useQuery<PlaygroundEvaluateRunResponse>({
    queryKey: playgroundEvaluateQueryKeys.run(workspaceId, sessionId, runId || ""),
    queryFn: () =>
      fetch(ENDPOINTS.playground_evaluate_runs(workspaceId, sessionId, runId || ""), {
        headers: jsonAuthHeaders(userToken),
      }).then(handlePlaygroundEvaluateResponse<PlaygroundEvaluateRunResponse>),
    enabled: Boolean(userToken && workspaceId && sessionId && runId),
    refetchInterval: data =>
      data?.run.status === "queued" || data?.run.status === "running" ? 1500 : false,
    onSuccess: data => {
      if (data.run.status === "queued" || data.run.status === "running") {
        return;
      }
      void queryClient.invalidateQueries({
        queryKey: playgroundEvaluateQueryKeys.state(workspaceId, sessionId),
      });
    },
  });
};
